import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { LoadingAnimation } from '@/shared/components/LoadingAnimation';
import { useNotification } from '@/shared/hooks/useNotification';
import { useApiError } from '@/shared/hooks/useApiError';
import { useConfirm } from '@/shared/contexts/confirm-context';
import { useAuthStore } from '@/features/auth/stores/authStore';
import {
  useFinancialYear,
  useFinancialYearPreflight,
  useFinancialYearSnapshot,
  useStartCloseFinancialYear,
  useCancelCloseFinancialYear,
  useConfirmCloseFinancialYear,
  useLockFinancialYear,
  useApproveFinancialYearCreation,
  useRejectFinancialYearCreation,
  useUpdateFinancialYear,
} from '../hooks/useFinancialYears';
import {
  FinancialYearStatus,
  type FinancialYearDto,
  type LockRequest,
  type PreflightCheckDto,
  type UpdateFinancialYearRequest,
} from '../services/financialYearsApi';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  ChevronRight,
  Lock,
  Pencil,
  XCircle,
} from 'lucide-react';
import { ConfirmCloseDialog } from '../components/financialYears/ConfirmCloseDialog';
import { LockDialog } from '../components/financialYears/LockDialog';
import { EditFinancialYearDialog } from '../components/financialYears/EditFinancialYearDialog';
import { PermissionGate } from '@/shared/components/PermissionGate';
import { ReasonDialog } from '@/features/invoicing/components/ReasonDialog';

const STATUS_BADGE: Record<FinancialYearStatus, { label: string; className: string }> = {
  [FinancialYearStatus.PendingApproval]: { label: 'Pending approval', className: 'bg-orange-100 text-orange-800 border-orange-300' },
  [FinancialYearStatus.Open]:            { label: 'Open',             className: 'bg-green-100 text-green-800 border-green-300' },
  [FinancialYearStatus.Closing]:         { label: 'Closing',          className: 'bg-amber-100 text-amber-800 border-amber-300' },
  [FinancialYearStatus.Closed]:          { label: 'Closed',           className: 'bg-blue-100 text-blue-800 border-blue-300' },
  [FinancialYearStatus.Locked]:          { label: 'Locked',           className: 'bg-purple-100 text-purple-800 border-purple-300' },
};

const formatDateTime = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('en-KE') : '—';
const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-KE', { year: 'numeric', month: 'short', day: 'numeric' });

export const FinancialYearDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const notify = useNotification();
  const reportError = useApiError();
  const confirm = useConfirm();
  const { hasPermission } = useAuthStore();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [lockOpen, setLockOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  const { data: fy, isLoading: fyLoading, error: fyError } = useFinancialYear(id);
  const { data: preflight, isLoading: preflightLoading } = useFinancialYearPreflight(id);
  const startCloseMutation = useStartCloseFinancialYear();
  const cancelCloseMutation = useCancelCloseFinancialYear();
  const confirmCloseMutation = useConfirmCloseFinancialYear();
  const lockMutation = useLockFinancialYear();
  const approveCreationMutation = useApproveFinancialYearCreation();
  const rejectCreationMutation = useRejectFinancialYearCreation();
  const updateMutation = useUpdateFinancialYear();

  if (fyLoading) return <LoadingAnimation fullScreen />;
  if (fyError || !fy) return <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-600">Financial year not found.</div>;

  const cfg = STATUS_BADGE[fy.status];
  const isPending = fy.status === FinancialYearStatus.PendingApproval;
  const isOpen = fy.status === FinancialYearStatus.Open;
  const isClosing = fy.status === FinancialYearStatus.Closing;
  const isClosed = fy.status === FinancialYearStatus.Closed;
  const isGm = hasPermission('financial_years.approve_creation');
  const canEdit = (isPending || isOpen) && (hasPermission('financial_years.edit') || isGm);

  const handleStartClose = () => {
    if (!fy) return;
    startCloseMutation.mutate(fy.id, {
      onSuccess: () => notify.success(`${fy.label} entered Closing state. Type the FY label to confirm or cancel to revert.`),
      onError: (err) => reportError(err),
    });
  };

  const handleConfirmClose = (closingNotes: string) => {
    if (!fy) return;
    confirmCloseMutation.mutate(
      { id: fy.id, payload: { confirmationLabel: fy.label, closingNotes } },
      {
        onSuccess: () => {
          notify.success(`${fy.label} closed. The year is now read-only.`);
          setConfirmOpen(false);
        },
        onError: (err) => reportError(err),
      },
    );
  };

  const handleCancelClose = () => {
    if (!fy) return;
    confirm({
      title: 'Cancel close?',
      description: `The year-end closing journal entry for ${fy.label} will be reversed and the snapshot cleared. ${fy.label} returns to Open.`,
      variant: 'destructive',
      confirmLabel: 'Cancel close',
      cancelLabel: 'Keep closing',
      onConfirm: () =>
        cancelCloseMutation
          .mutateAsync(fy.id)
          .then(() => notify.success(`Close of ${fy.label} cancelled. Year is back to Open.`))
          .catch((err) => {
            reportError(err);
            throw err;
          }),
    });
  };

  const handleLock = (payload: LockRequest) => {
    if (!fy) return;
    lockMutation.mutate(
      { id: fy.id, payload },
      {
        onSuccess: () => {
          notify.success(`${fy.label} locked. The year is now audited and immutable.`);
          setLockOpen(false);
        },
        onError: (err) => reportError(err),
      },
    );
  };

  const handleApproveCreation = () => {
    if (!fy) return;
    confirm({
      title: 'Approve financial year?',
      description: `${fy.label} will become Open and ready for posting.`,
      confirmLabel: 'Approve',
      onConfirm: () =>
        approveCreationMutation
          .mutateAsync(fy.id)
          .then(() => notify.success(`${fy.label} approved and is now Open.`))
          .catch((err) => {
            reportError(err);
            throw err;
          }),
    });
  };

  const handleRejectSubmit = (reason: string) => {
    if (!fy) return;
    rejectCreationMutation.mutate(
      { id: fy.id, reason },
      {
        onSuccess: () => {
          notify.success(`${fy.label} rejected. The maker can re-create with corrected details.`);
          setRejectOpen(false);
        },
        onError: (err) => reportError(err),
      }
    );
  };

  const handleUpdate = (payload: UpdateFinancialYearRequest, gmOverride: boolean) => {
    if (!fy) return;
    updateMutation.mutate(
      { id: fy.id, payload, gmOverride },
      {
        onSuccess: (updated) => {
          notify.success(`${updated.label} updated.`);
          setEditOpen(false);
        },
        onError: (err) => reportError(err),
      },
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Link to="/accounting/financial-years" className="hover:text-foreground inline-flex items-center gap-1">
          <ArrowLeft className="size-4" /> Financial years
        </Link>
        <ChevronRight className="size-3" />
        <span>{fy.label}</span>
      </div>

      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{fy.label}</h1>
          <p className="text-muted-foreground">
            {formatDate(fy.startDate)} → {formatDate(fy.endDate)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
              <Pencil className="size-4 mr-1" /> Edit
            </Button>
          )}
          <Badge variant="outline" className={`${cfg.className} text-base px-3 py-1`}>
            {fy.status === FinancialYearStatus.Locked && <Lock className="size-4 mr-1" />}
            {cfg.label}
          </Badge>
        </div>
      </div>

      {fy.status !== FinancialYearStatus.Open && fy.status !== FinancialYearStatus.PendingApproval && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-6 text-sm text-blue-900">
            <strong>This year is closed for posting.</strong> To correct an item, post an adjusting journal
            entry in the current open year with a narration referencing this period.
            {fy.status === FinancialYearStatus.Locked && fy.lockedAt && (
              <> Audited and signed off on {formatDateTime(fy.lockedAt)}.</>
            )}
          </CardContent>
        </Card>
      )}

      {isPending && (
        <Card className="border-orange-200 bg-orange-50">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="size-5 text-orange-700" /> Pending GM approval
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <p>
              This financial year was created and is awaiting approval before it can be used for posting.
              Posts dated inside this year are refused until a General Manager approves it.
            </p>
            <PermissionGate permissions={['financial_years.approve_creation']}>
              <div className="flex gap-2">
                <Button
                  onClick={handleApproveCreation}
                  disabled={approveCreationMutation.isPending || rejectCreationMutation.isPending}
                >
                  Approve
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setRejectOpen(true)}
                  disabled={approveCreationMutation.isPending || rejectCreationMutation.isPending}
                >
                  Reject
                </Button>
              </div>
            </PermissionGate>
          </CardContent>
        </Card>
      )}

      {isOpen && (
        <PreflightCard
          loading={preflightLoading}
          checks={preflight?.checks ?? []}
          allBlockingPassed={preflight?.allBlockingPassed ?? false}
          onStartClose={handleStartClose}
          starting={startCloseMutation.isPending}
        />
      )}

      {isClosing && (
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="size-5 text-amber-700" /> Close in progress
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm">
              The year-end closing journal entry has been posted and the snapshot frozen. To finalise, type the FY
              label exactly to confirm — close is irreversible.
            </p>
            <div className="flex gap-3">
              <Button onClick={() => setConfirmOpen(true)} disabled={confirmCloseMutation.isPending}>
                Confirm Close
              </Button>
              <Button
                variant="outline"
                onClick={handleCancelClose}
                disabled={cancelCloseMutation.isPending}
              >
                {cancelCloseMutation.isPending ? 'Cancelling…' : 'Cancel Close'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {isClosed && (
        <Card className="border-purple-200">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Lock className="size-5 text-purple-700" /> Audit sign-off
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Once the auditor has signed off on this year, lock it to mark the books as final. Locking is
              terminal — no permission unlocks. Adjustments after lock must be posted in the current open year.
            </p>
          </CardHeader>
          <CardContent>
            <PermissionGate permissions={['financial_years.lock']}>
              <Button
                onClick={() => setLockOpen(true)}
                disabled={lockMutation.isPending}
                className="bg-purple-700 hover:bg-purple-800"
              >
                <Lock className="size-4 mr-2" /> Lock {fy.label}
              </Button>
            </PermissionGate>
          </CardContent>
        </Card>
      )}

      <FYDetailsCard fy={fy} />

      {(fy.status === FinancialYearStatus.Closing || fy.status === FinancialYearStatus.Closed || fy.status === FinancialYearStatus.Locked) && (
        <SnapshotCard id={fy.id} />
      )}

      <ConfirmCloseDialog
        open={confirmOpen}
        fyLabel={fy.label}
        onOpenChange={setConfirmOpen}
        onSubmit={handleConfirmClose}
        submitting={confirmCloseMutation.isPending}
      />

      <LockDialog
        open={lockOpen}
        fyLabel={fy.label}
        onOpenChange={setLockOpen}
        onSubmit={handleLock}
        submitting={lockMutation.isPending}
      />

      {editOpen && fy && (
        <EditFinancialYearDialog
          open={editOpen}
          fy={fy}
          isGm={isGm}
          onOpenChange={setEditOpen}
          onSubmit={handleUpdate}
          submitting={updateMutation.isPending}
        />
      )}

      <ReasonDialog
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        title={`Reject ${fy.label}?`}
        description="Provide a reason so the maker understands what to fix. The year can be re-created with corrected details."
        reasonLabel="Rejection reason"
        reasonPlaceholder="Why is this financial year being rejected?"
        submitLabel="Reject"
        isSubmitting={rejectCreationMutation.isPending}
        onSubmit={handleRejectSubmit}
      />
    </div>
  );
};

const PreflightCard = ({
  loading,
  checks,
  allBlockingPassed,
  onStartClose,
  starting,
}: {
  loading: boolean;
  checks: PreflightCheckDto[];
  allBlockingPassed: boolean;
  onStartClose: () => void;
  starting: boolean;
}) => (
  <Card>
    <CardHeader>
      <CardTitle className="text-base">Pre-close checklist</CardTitle>
      <p className="text-sm text-muted-foreground">
        Advisory — review remaining work before closing. The company decides when to close;
        these items do not block the action. The list refreshes every 15 seconds.
      </p>
    </CardHeader>
    <CardContent className="space-y-3">
      {loading && <p className="text-sm text-muted-foreground">Running checks…</p>}
      {!loading && checks.map((c) => (
        <div
          key={c.id}
          className="flex items-start justify-between gap-3 border rounded-md px-3 py-2 bg-card"
        >
          <div className="flex items-start gap-3">
            {c.passed ? (
              <CheckCircle2 className="size-5 text-green-600 mt-0.5 shrink-0" />
            ) : (
              <XCircle className="size-5 text-red-600 mt-0.5 shrink-0" />
            )}
            <div>
              <div className="text-sm font-medium">{c.label}</div>
              {!c.passed && c.failingCount > 0 && (
                <div className="text-xs text-muted-foreground">
                  {c.failingCount} {c.failingCount === 1 ? 'item' : 'items'} unresolved
                </div>
              )}
            </div>
          </div>
          {!c.passed && c.linkUrl && (
            <Button asChild variant="link" size="sm">
              <Link to={c.linkUrl}>Resolve</Link>
            </Button>
          )}
        </div>
      ))}

      <div className="flex items-center justify-between pt-2 border-t">
        <div className="text-sm">
          {allBlockingPassed ? (
            <span className="text-green-700 font-medium">All checks passed.</span>
          ) : (
            <span className="text-amber-700">Some checks haven't passed — close anyway if the company has decided to.</span>
          )}
        </div>
        <Button onClick={onStartClose} disabled={starting}>
          {starting ? 'Starting close…' : 'Start Close'}
        </Button>
      </div>
    </CardContent>
  </Card>
);

const FYDetailsCard = ({ fy }: { fy: FinancialYearDto }) => (
  <Card>
    <CardHeader>
      <CardTitle className="text-base">Audit trail</CardTitle>
    </CardHeader>
    <CardContent className="space-y-2 text-sm">
      <DetailRow label="Closing initiated" value={formatDateTime(fy.closingInitiatedAt)} />
      <DetailRow label="Closed at" value={formatDateTime(fy.closedAt)} />
      <DetailRow label="Closing notes" value={fy.closingNotes ?? '—'} />
      {fy.status === FinancialYearStatus.Locked && (
        <>
          <DetailRow label="Locked at" value={formatDateTime(fy.lockedAt)} />
          <DetailRow label="Audit firm" value={fy.auditFirmName ?? '—'} />
          <DetailRow label="Audit reference" value={fy.auditReportReference ?? '—'} />
        </>
      )}
    </CardContent>
  </Card>
);

const DetailRow = ({ label, value }: { label: string; value: string }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
    <div className="text-muted-foreground">{label}</div>
    <div className="col-span-2">{value}</div>
  </div>
);

const SnapshotCard = ({ id }: { id: string }) => {
  const { data, isLoading, error } = useFinancialYearSnapshot(id);

  if (isLoading) return null;
  if (error) return null; // 404 is expected for years that haven't started closing

  if (!data) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Closing snapshot</CardTitle>
        <p className="text-sm text-muted-foreground">
          Frozen on {formatDateTime(data.frozenAt)}. {data.trialBalance.length} accounts in the trial balance.
        </p>
      </CardHeader>
      <CardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
        <Stat label="Total income" value={data.totalIncome.toLocaleString()} />
        <Stat label="Total expense" value={data.totalExpense.toLocaleString()} />
        <Stat
          label={data.netResult >= 0 ? 'Net profit' : 'Net loss'}
          value={Math.abs(data.netResult).toLocaleString()}
          highlight
        />
      </CardContent>
    </Card>
  );
};

const Stat = ({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) => (
  <div>
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className={`text-xl font-semibold ${highlight ? 'text-blue-700' : ''}`}>{value}</div>
  </div>
);
